import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";

import { computeStreak } from "@/lib/analytics/engine";
import { sleepHourOf, stdDev, windowStats, sleepDebt as calculateSleepDebt, type TrendNight } from "@/lib/sleep/trends";
import { sleepDurationValue } from "@/lib/sleep/values";
import { useSleepResource, notifySleepChanged } from "@/hooks/useSleepResource";
import { invokeSleepAction, executeReviewedSleepAction, acknowledgeSleepAction, readPendingSleepAction, newSleepOperationId, sleepOperationKey, sameSleepRequest, snapshotForSleep, snapshotForSleepPreferences, type SleepRequest, type SleepSnapshot, type SleepPreferencesSnapshot, type SleepResponse } from '@/lib/sleepExecutor';
import { canonicalSleepRecord, validSleepDayMetadata, SLEEP_NOTES } from '../../supabase/functions/_shared/sleepActionDomain';
import type { PendingConnectorWrite } from '@/lib/connectorPendingWrite';

export type SleepQuality = "amazing" | "good" | "fair" | "poor" | "terrible";
export type SleepNote = "caffeine" | "alcohol" | "exercise" | "stress" | "screen_time" | "late_meal" | "medication" | "nap";

export interface SleepLogPayload {
  bedtime: string;
  wakeup: string;
  quality: SleepQuality;
  notes: SleepNote[];
  note_text?: string;
  // Provenance — defaults to 'manual'. The Mirror voice-capture flow passes
  // 'voice'. Existing callers omit it (stay 'manual').
  source?: string;
  // true = o usuário está CORRIGINDO uma noite existente. Carimba edited_at,
  // que é o que impede o sync nativo de reescrever a correção por cima.
  edited?: boolean;
}

export interface SleepLog {
  id: string;
  user_id: string;
  sleep_date: string;
  bedtime: string;
  wakeup: string;
  duration_hours: number | null;
  score: number | null;
  quality: SleepQuality | null;
  notes: SleepNote[] | null;
  note_text: string | null;
  sleep_debt_minutes: number | null;
  source: string;
  // A coluna sempre existiu no banco e faltava aqui. Passou a importar quando
  // a exclusão virou durável: a lápide precisa do external_id para o sync não
  // ressuscitar a noite (ver lib/health/deleteImported.ts).
  external_id?: string | null;
  created_at?: string | null;
  edited_at?: string | null;
  snapshot?: SleepSnapshot | null;
  writable?: boolean;
  read_only?: boolean;
  content_truncated?: boolean;
}

export interface NoteInsight {
  note: string;
  avgWith: number;
  avgWithout: number;
}

export interface SleepStats {
  avgDuration: number | null;
  avgScore: number | null;
  sleepDebt: number | null; // minutes, unknown when no duration or invalid target
  durationNights: number;
  scoreNights: number;
  consistency: number; // std dev of bedtime hour
  streak: number;
  weekLogs: (SleepLog | null)[];
  weekDayLabels: string[];
  topNotes: NoteInsight[];
}

export function calculateScore(durationHours: number, targetHours: number): number {
  const ratio = durationHours / targetHours;
  return Math.min(
    100,
    Math.round(
      ratio >= 1
        ? 85 + Math.min(15, (ratio - 1) * 30)
        : ratio >= 0.85
          ? 70 + Math.round((ratio - 0.85) * 100)
          : Math.round(ratio * 82)
    )
  );
}

export function calculateDuration(bedtime: string, wakeup: string): number {
  if (![bedtime, wakeup].every(value => /^([01]\d|2[0-3]):[0-5]\d$/.test(value))) return NaN;
  const [bH, bM] = bedtime.split(":").map(Number);
  const [wH, wM] = wakeup.split(":").map(Number);
  const bedMin = bH * 60 + bM;
  let wakeMin = wH * 60 + wM;
  if (wakeMin < bedMin) wakeMin += 24 * 60;
  return parseFloat(((wakeMin - bedMin) / 60).toFixed(2));
}



export function sleepDate(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export interface SleepPreferences {
  sleep_target_hours: string | null;
  sleep_bedtime_reminder: boolean | null;
  sleep_bedtime_reminder_time: string | null;
  sleep_wakeup_reminder: boolean | null;
  sleep_wakeup_reminder_time: string | null;
}
const PREF_COLUMNS = "sleep_target_hours,sleep_bedtime_reminder,sleep_bedtime_reminder_time,sleep_wakeup_reminder,sleep_wakeup_reminder_time";
const shiftSleepDay = (day: string, shift: number) => { const date = new Date(day + 'T12:00:00Z'); date.setUTCDate(date.getUTCDate() + shift); return date.toISOString().slice(0, 10); };
export interface SleepReview { sleep_date: string; expected_snapshot: SleepSnapshot | null }
async function readSleepLogs(uid: string, days: number, assertCurrent: () => void, day = sleepDate()): Promise<SleepLog[]> {
  const { data, error } = await supabase.from("sleep_logs").select("*").eq("user_id", uid)
    .gte("sleep_date", shiftSleepDay(day, 1-days)).lte("sleep_date", day).order("sleep_date", { ascending: false });
  assertCurrent(); if (error) throw error;
  return (data ?? []).map(row => canonicalSleepRecord({ sleep: row }, uid)) as unknown as SleepLog[];
}
async function loadSleep(uid: string, assertCurrent: () => void) {
  const metadata = await invokeSleepAction({ action: 'get_preferences' }, uid, assertCurrent);
  if (!validSleepDayMetadata(metadata)) throw new Error('Sleep day not confirmed');
  const logs = await readSleepLogs(uid, 30, assertCurrent, metadata.sleep_day);
  const snapshot = snapshotForSleepPreferences(metadata.preferences, uid);
  return { logs, preferences: snapshot?.preferences as SleepPreferences | undefined ?? null,
    preferencesSnapshot: snapshot, target: metadata.target_hours, day: metadata.sleep_day,
    timezone: metadata.timezone, timezoneSource: metadata.timezone_source };
}

export function useSleep() {
  const { t } = useLanguage();
  const resource = useSleepResource(loadSleep);
  const targetHours = resource.data?.target ?? 8;
  const lastNightSleep = resource.data?.logs.find(log => log.sleep_date === (resource.data?.day ?? sleepDate())) ?? null;
  const busy = useRef<symbol | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingSleep, setPendingSleep] = useState<PendingConnectorWrite | null>(null);
  useEffect(() => { busy.current = null; setIsSaving(false); setPendingSleep(null); }, [resource.userId, resource.generation]);
  const stats = useMemo(() => {
    const logs = resource.data?.logs ?? [];
    const target = targetHours;
    const dayNames = [t("common.days.sun"), t("common.days.mon"), t("common.days.tue"), t("common.days.wed"), t("common.days.thu"), t("common.days.fri"), t("common.days.sat")];
    const getDayLabels = () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(shiftSleepDay(resource.data?.day ?? sleepDate(), -6+i) + "T12:00:00Z"); return dayNames[d.getUTCDay()];
    });
      // Build week logs (last 7 days)
      const weekLogs: (SleepLog | null)[] = Array.from({ length: 7 }, (_, i) => {
        const dateStr = shiftSleepDay(resource.data?.day ?? sleepDate(), i-6);
        return logs.find(l => l.sleep_date === dateStr) || null;
      });

      const weekWithData = weekLogs.filter((l): l is SleepLog => l !== null);

      // The hook and trend cards share both arithmetic and valid denominators.
      // Anchor their civil window to the proven server day, not the device day.
      const trendNow = new Date((resource.data?.day ?? sleepDate()) + "T12:00:00").getTime();
      const trendNights: TrendNight[] = weekWithData.map(log => ({ date: log.sleep_date,
        bedtime: log.bedtime, wakeup: log.wakeup, durationHours: log.duration_hours, score: log.score }));
      const averages = windowStats(trendNights, 7, trendNow);
      const debt = calculateSleepDebt(trendNights, target, 7, trendNow);

      // Consistency: std dev of bedtime hour
      // UMA definição de "hora de dormir na reta" e UMA de desvio-padrão, as
      // duas em lib/sleep/trends.ts. Estavam aqui, inline, e a tela de
      // Insights precisava exatamente delas — copiar seria garantir que as
      // duas divergissem na primeira edição.
      // ⚠️ `?? 0` preserva o comportamento antigo: menos de 2 noites era 0.
      const bedtimeHours = weekWithData
        .filter(l => l.bedtime)
        .map(l => sleepHourOf(l.bedtime.slice(0, 5)))
        .filter((v): v is number => v !== null);
      const consistency = stdDev(bedtimeHours) ?? 0;

      // Streak — ONE canonical definition (analytics engine). sleep_date is a
      // date-only column; parse at LOCAL noon so day bucketing never drifts
      // across the UTC boundary. Now spans the full fetch, not just this week.
      const streak = computeStreak(
        logs.map((l) => new Date(l.sleep_date + "T12:00:00").getTime()),
        { now: new Date((resource.data?.day ?? sleepDate()) + "T12:00:00").getTime() },
      ).current;

      // Unknown notes cannot enter the "without" group. Both groups must
      // consist of valid durations and an observed note list (including []).
      const usableNotes = logs.flatMap(log => {
        const duration = sleepDurationValue(log.duration_hours);
        return duration !== null && log.content_truncated !== true && Array.isArray(log.notes) && log.notes.every(note => SLEEP_NOTES.includes(note)) ? [{ duration, notes: log.notes }] : [];
      });
      const topNotes: NoteInsight[] = [];
      if (usableNotes.length >= 7) {
        const allNotes = new Set<string>();
        usableNotes.forEach(l => l.notes.forEach(n => allNotes.add(n)));
        allNotes.forEach(note => {
          const withNote = usableNotes.filter(l => l.notes.includes(note as SleepNote));
          const withoutNote = usableNotes.filter(l => !l.notes.includes(note as SleepNote));
          if (withNote.length >= 2 && withoutNote.length >= 2) {
            const avgWith = withNote.reduce((s, l) => s + l.duration, 0) / withNote.length;
            const avgWithout = withoutNote.reduce((s, l) => s + l.duration, 0) / withoutNote.length;
            topNotes.push({ note, avgWith: parseFloat(avgWith.toFixed(1)), avgWithout: parseFloat(avgWithout.toFixed(1)) });
          }
        });
        topNotes.sort((a, b) => (a.avgWith - a.avgWithout) - (b.avgWith - b.avgWithout));
      }


    return { avgDuration: averages.avgDuration, avgScore: averages.avgScore,
      sleepDebt: debt.minutes, durationNights: averages.durationNights, scoreNights: averages.scoreNights,
      consistency: Number(consistency.toFixed(2)),
      streak, weekLogs, weekDayLabels: getDayLabels(), topNotes };
  }, [resource.data, targetHours, t]);

  const identity = useCallback(async () => {
    const current = resource.capture();
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user || user.id !== resource.userId || !current()) throw error ?? new Error("Sleep session changed");
    return { uid: user.id, assertCurrent: () => { if (!current()) throw new Error("Sleep session changed"); } };
  }, [resource.userId, resource.capture]);
  const mutate = useCallback(async (request: SleepRequest): Promise<SleepResponse> => {
    if (busy.current) throw new Error('Sleep save already in progress');
    const original = structuredClone(request), operation = Symbol(); busy.current = operation; setIsSaving(true);
    try {
      const { uid, assertCurrent } = await identity();
      const pending = await readPendingSleepAction(uid, assertCurrent); assertCurrent();
      const intent = (body: SleepRequest) => { const { operation_id: _id, idempotency_key: _key, ...rest } = body; return rest; };
      let frozen = original;
      if (pending) { if (pending.body.mirror_context || !sameSleepRequest(intent(pending.body as unknown as SleepRequest), intent(original))) throw new Error('sleep_pending_action'); frozen = structuredClone(pending.body) as unknown as SleepRequest; }
      const done = await executeReviewedSleepAction(uid, frozen, assertCurrent); assertCurrent(); setPendingSleep(done.entry);
      try { await acknowledgeSleepAction(done.entry, assertCurrent); assertCurrent(); setPendingSleep(null); } catch (cause) { assertCurrent(); console.warn('[Sleep] confirmed receipt awaiting acknowledgement', cause instanceof Error ? cause.message : 'unknown'); }
      await resource.refresh(); assertCurrent(); notifySleepChanged(); return done.response;
    } catch (cause) { try { const { uid, assertCurrent } = await identity(); const pending = await readPendingSleepAction(uid, assertCurrent); assertCurrent(); setPendingSleep(pending); } catch { /* Original error remains authoritative. */ } throw cause; }
    finally { if (busy.current === operation) { busy.current = null; setIsSaving(false); } }
  }, [identity, resource.refresh]);
  const saveSleepLog = useCallback(async (payload: SleepLogPayload, review?: SleepReview) => {
    const owner = resource.userId, day = review?.sleep_date ?? resource.data?.day;
    if (!owner || !day) throw new Error('Sleep session changed');
    const shown = review ? structuredClone(review) : { sleep_date: day, expected_snapshot: lastNightSleep ? snapshotForSleep(lastNightSleep, owner) : null };
    if (!review && lastNightSleep && !shown.expected_snapshot) throw new Error('sleep_read_only');
    const fields = { bedtime: payload.bedtime, wakeup: payload.wakeup, quality: payload.quality, notes: payload.notes, ...(payload.note_text !== undefined ? { note_text: payload.note_text.trim() || null } : {}) };
    const action = shown.expected_snapshot ? 'update' : 'create', operation_id = newSleepOperationId();
    const response = await mutate({ action, operation_id, idempotency_key: sleepOperationKey(operation_id, action), confirmed: true,
      expected_snapshot: shown.expected_snapshot, ...(shown.expected_snapshot ? { sleep_id: shown.expected_snapshot.sleep.id, fields } : { fields: { ...fields, sleep_date: day } }) });
    if (!response.record) throw new Error('Sleep save not confirmed');
    toast.success(t('sleep.saved')); return { log: response.record as unknown as SleepLog, xp: response.xp, response };
  }, [resource.userId, resource.data, lastNightSleep, mutate, t]);
  const updateSleepLog = useCallback(async (log: SleepLog, payload: Partial<SleepLogPayload>) => {
    const snapshot = snapshotForSleep(structuredClone(log), resource.userId ?? undefined); if (!snapshot) throw new Error('sleep_read_only');
    const fields = Object.fromEntries(Object.entries(payload).filter(([key,value]) => ['bedtime','wakeup','quality','notes','note_text'].includes(key) && value !== undefined));
    const operation_id = newSleepOperationId(); return mutate({ action:'update', operation_id, idempotency_key:sleepOperationKey(operation_id,'update'),confirmed:true,sleep_id:log.id,expected_snapshot:snapshot,fields });
  }, [resource.userId, mutate]);
  const deleteSleepLog = useCallback(async (log: SleepLog) => {
    const snapshot = snapshotForSleep(structuredClone(log), resource.userId ?? undefined); if (!snapshot) throw new Error('sleep_read_only');
    const operation_id = newSleepOperationId(); return mutate({ action:'delete',operation_id,idempotency_key:sleepOperationKey(operation_id,'delete'),confirmed:true,sleep_id:log.id,expected_snapshot:snapshot });
  }, [resource.userId, mutate]);
  const savePreferences = useCallback(async (prefs: Partial<SleepPreferences>, review?: SleepPreferencesSnapshot|null) => {
    const operation_id = newSleepOperationId(), snapshot = review === undefined ? resource.data?.preferencesSnapshot ?? null : structuredClone(review);
    return mutate({ action:'save_preferences', operation_id, idempotency_key:sleepOperationKey(operation_id,'save_preferences'),confirmed:true,preferences_patch:structuredClone(prefs),expected_preferences_snapshot:snapshot });
  }, [resource.data, mutate]);
  const getSleepHistory = useCallback(async (days: number) => {
    if (!Number.isInteger(days) || days < 1 || days > 365) throw new Error("Invalid sleep history window");
    const { uid, assertCurrent } = await identity();
    return readSleepLogs(uid, days, assertCurrent, resource.data?.day ?? sleepDate());
  }, [identity, resource.data]);
  return { lastNightSleep, isLoading: resource.status === "loading", stats, targetHours,
    preferences: resource.data?.preferences, preferencesSnapshot: resource.data?.preferencesSnapshot, sleepDay: resource.data?.day, timezone: resource.data?.timezone, timezoneSource: resource.data?.timezoneSource, pendingSleep, hasData: resource.data !== null,
    userId: resource.userId, identityGeneration: resource.generation, status: resource.status, checkedAt: resource.checkedAt, isRefreshing: resource.isRefreshing,
    capture: resource.capture, isSaving, saveSleepLog, updateSleepLog, deleteSleepLog, savePreferences,
    getSleepHistory, refresh: resource.refresh };
}
